import { useRole } from '../hooks/useRole';
import React, { type ComponentType } from 'react';
import { Alert, AlertTitle, Typography } from '@mui/material';

interface AuthorizationGuardProps {
	Component: ComponentType<{
		backwardRoute?: string; // Optional backwardRoute type
	}>;
	everyoneAccess?: boolean;
	userAccess?: boolean;
	staffAccess?: boolean;
	supplierAccess?: boolean;
	adminAccess?: boolean;
	contractorAccess?: boolean;
	excludeStaff?: boolean;
	excludeContractor?: boolean;
	backwardRoute?: string;
}

export default function AuthorizationGuard({
	Component,
	everyoneAccess = false,
	userAccess = false,
	staffAccess = false,
	supplierAccess = false,
	adminAccess = false,
	contractorAccess = false,
	excludeStaff = false,
	excludeContractor = false,
	backwardRoute,
}: AuthorizationGuardProps) {
	const { isAdmin, isUser, isStaff, isSupplier, isContractor } = useRole();

	const denyView = (
		<Alert severity="error">
			<AlertTitle sx={{ typography: 'h6' }}>Error</AlertTitle>
			<Typography>You don't have sufficient permissions to access this page.</Typography>
		</Alert>
	);

	if (isAdmin) {
		return <Component {...{ backwardRoute }} />;
	}

	if (excludeStaff && isStaff) {
		return denyView;
	}

	if (excludeContractor && isContractor) {
		return denyView;
	}

	if (everyoneAccess) {
		return <Component {...{ backwardRoute }} />;
	}

	if (adminAccess) {
		return denyView;
	}

	if (userAccess) {
		return isUser ? <Component {...{ backwardRoute }} /> : denyView;
	}

	if (staffAccess) {
		return isStaff ? <Component {...{ backwardRoute }} /> : denyView;
	}

	if (supplierAccess) {
		return isSupplier ? <Component {...{ backwardRoute }} /> : denyView;
	}

	if (contractorAccess) {
		return isContractor ? <Component {...{ backwardRoute }} /> : denyView;
	}

	return denyView; // Fallback for cases where none of the conditions match
}
